import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, input, output, signal, ViewChild } from '@angular/core';
import { DsyInput } from '@dsy/enelx';
import { FilterEntry } from '../../../models/filters.model';
import { HighAlertFilterOptions, HighAlertFilterValues } from '../../../models/high-alert-filter.model';
import { SearchableDropdownComponent } from '../../../shared/components/searchable-dropdown/searchable-dropdown.component';


@Component({
  selector: 'dr-ops-stream-ui-high-alerts-filters',
  templateUrl: './high-alerts-filters.component.html',
  standalone: true,
  imports: [SearchableDropdownComponent],
  styleUrls: ['./high-alerts-filters.component.scss', '../../../shared/styles/filters.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HighAlertsFiltersComponent {
  @ViewChild('startDateInput', { read: ElementRef }) startDateInput!: ElementRef<DsyInput>;
  @ViewChild('endDateInput', { read: ElementRef }) endDateInput!: ElementRef<DsyInput>;

  readonly options = input.required<HighAlertFilterOptions>();
  readonly filtersChange = output<HighAlertFilterValues>();

  readonly programSignal = signal<string | null>(null);
  readonly windowSignal = signal<string | null>(null);
  readonly createdBySignal = signal<string | null>(null);
  readonly startDateSignal = signal<Date | null>(null);
  readonly endDateSignal = signal<Date | null>(null);

  private hasAppliedFilters: boolean = false;

  readonly currentFiltersSignal = computed<HighAlertFilterValues>(() => ({
    program: this.programSignal(),
    window: this.windowSignal(),
    createdBy: this.createdBySignal(),
    startDate: this.startDateSignal(),
    endDate: this.endDateSignal(),
    status: null,
  }));

  readonly invalidDateRangeSignal = computed<boolean>(() => {
    const startDate = this.startDateSignal();
    const endDate = this.endDateSignal();
    if (!startDate || !endDate) return false;
    return startDate.getTime() > endDate.getTime();
  });

  onProgramChange = (value: string): void => this.programSignal.set(value);
  onWindowChange = (value: string): void => this.windowSignal.set(value);
  onCreatedByChange = (value: string): void => this.createdBySignal.set(value);
  onStartDateChange = (value: string): void => this.startDateSignal.set(value ? new Date(value) : null);
  onEndDateChange = (value: string): void => this.endDateSignal.set(value ? new Date(value) : null);

  readonly applyButtonDisabledSignal = computed(
    () =>
      (!this.currentFiltersSignal().program &&
        !this.currentFiltersSignal().window &&
        !this.currentFiltersSignal().createdBy &&
        !this.currentFiltersSignal().startDate &&
        !this.currentFiltersSignal().endDate) ||
      this.invalidDateRangeSignal()
  );

  readonly programFilterEntriesSignal = computed<FilterEntry[]>(() => this.createFilterEntries(this.options().programs, this.programSignal()));
  readonly windowFilterEntriesSignal = computed<FilterEntry[]>(() => this.createFilterEntries(this.options().windows ?? [], this.windowSignal()));
  readonly createdByFilterEntriesSignal = computed<FilterEntry[]>(() => this.createFilterEntries(this.options().creators, this.createdBySignal()));

  private createFilterEntries(labels: string[], currentValue: string | null): FilterEntry[] {
    return labels.map((label, i) => ({
      id: i + 1,
      labelContent: label,
      isSelected: currentValue === label,
    }));
  }

  onReset(): void {
    this.programSignal.set(null);
    this.windowSignal.set(null);
    this.createdBySignal.set(null);
    this.startDateSignal.set(null);
    this.endDateSignal.set(null);
    this.clearDate(this.startDateInput);
    this.clearDate(this.endDateInput);
    if (this.hasAppliedFilters) {
      this.filtersChange.emit(this.currentFiltersSignal());
      this.hasAppliedFilters = false;
    }
  }

  private clearDate(viewChild?: ElementRef<DsyInput>): void {
    if (!viewChild) return;
    const inputField = viewChild.nativeElement.inputField?.value;
    if (inputField instanceof HTMLInputElement && inputField.value) {
      inputField.value = '';
    }
  }

  onApply(): void {
    if (this.invalidDateRangeSignal()) return;
    this.filtersChange.emit(this.currentFiltersSignal());
    this.hasAppliedFilters = true;
  }
}
